
import React, { useEffect, useState } from 'react';
import styles from './modulestyle.module.css';
import { useForm } from 'react-hook-form';
import axios from 'axios';

const baseUrl = 'https://api.codedmastery.com';
// const baseUrl = 'http://localhost:5000';
const moduleEndpoint = `${baseUrl}/modules`;

const Modules = () => {
  const [courses, setCourses] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const token = localStorage.getItem('token');

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      courseId: '',
      title: '',
      description: '',
      order: '',
    },
  });

  const selectedCourseId = watch('courseId');
  const selectedCourse = courses.find((course) => course._id === selectedCourseId) || null;

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`${baseUrl}/courses`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        const coursesData = await response.json();
        setCourses(coursesData || []);
      } catch (err) {
        console.error('Failed to load data:', err);
        setFeedback({ type: 'error', message: 'Unable to load available courses.' });
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [token]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setFeedback(null);

    const payload = {
      courseId: data.courseId,
      title: data.title.trim(),
      description: data.description.trim(),
      order: data.order === '' ? 0 : Number(data.order),
    };

    try {
      await axios.post(moduleEndpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      setFeedback({ type: 'success', message: 'Module created successfully.' });
      reset();
    } catch (error) {
      console.error(error);
      setFeedback({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Unable to create module.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingData) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingCard}>
          <div className={styles.loader} />
          <p>Loading course options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageShell}>
      <section className={styles.heroCard}>
        <div>
          <p className={styles.eyebrow}>Admin workspace</p>
          <h1>Create a new module</h1>
          <p className={styles.heroText}>
            Build structured learning modules by linking them to a course and ordering them for the learner journey.
          </p>
        </div>
        <div className={styles.heroBadge}>Module schema ready</div>
      </section>

      <div className={styles.bodySection}>
        <section className={styles.formPanel}>
          <div className={styles.panelHeader}>
            <h2>Module details</h2>
            <p>Required fields are marked to keep the creation flow simple and consistent.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className={styles.formGrid}>
            <div className={styles.fieldGroup}>
              <label htmlFor="courseId">Course</label>
              <select id="courseId" {...register('courseId', { required: true })} className={styles.inputField}>
                <option value="">Select a course</option>
                {courses.map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.title}
                  </option>
                ))}
              </select>
              {errors.courseId && <span className={styles.errorText}>Course is required</span>}
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="title">Module title</label>
              <input
                id="title"
                type="text"
                placeholder="Enter module title"
                {...register('title', { required: true })}
                className={styles.inputField}
              />
              {errors.title && <span className={styles.errorText}>Title is required</span>}
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                placeholder="Describe what learners will cover in this module"
                {...register('description')}
                className={styles.textArea}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="order">Display order</label>
              <input
                id="order"
                type="number"
                min="0"
                placeholder="0"
                {...register('order')}
                className={styles.inputField}
              />
            </div>

            {feedback && (
              <div className={`${styles.feedback} ${feedback.type === 'success' ? styles.success : styles.error}`}>
                {feedback.message}
              </div>
            )}

            <button type="submit" className={styles.submitButton} disabled={submitting}>
              {submitting ? 'Creating module...' : 'Create module'}
            </button>
          </form>
        </section>

        <aside className={styles.previewPanel}>
          <div className={styles.previewCard}>
            <p className={styles.previewLabel}>Module preview</p>
            <div className={styles.previewItem}>
              <span>Selected course</span>
              <strong>{selectedCourse ? selectedCourse.title : 'No course selected'}</strong>
            </div>
            <div className={styles.previewItem}>
              <span>Module title</span>
              <strong>{watch('title') || 'Your module title'}</strong>
            </div>
            <div className={styles.previewItem}>
              <span>Display order</span>
              <strong>{watch('order') || '0'}</strong>
            </div>
          </div>

          <div className={styles.tipCard}>
            <h3>Schema alignment</h3>
            <ul>
              <li>courseId links the module to a course</li>
              <li>title is required for every module</li>
              <li>description and order are optional and can be left blank</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Modules;
